// ثبتِ رویدادِ رفتاری — جدولِ `app_events` (مایگریشن 0068).
//
// منطقِ خالص (پاکسازی، صف، شناسه‌ی نشست) در `analyticsQueue.ts` است و با
// `npm test` آزموده می‌شود. این فایل فقط لایه‌ی عارضه‌دار است: تایمر و شبکه.
//
// ═══ سه قاعده که شکلِ این کد را تعیین کرده‌اند ═══
//
// ۱. **هرگز مزاحمِ کاربر نشود.** `track()` منتظر نمی‌ماند و خطا پرت نمی‌کند.
//    آماری که یک ثانیه به ساختِ سیگنال اضافه کند ارزشش را ندارد؛ آماری که
//    ساختِ سیگنال را بشکند فاجعه است.
//
// ۲. **هیچ داده‌ی شخصی‌ای در `props` نرود.** دو لایه جلویش را می‌گیرد:
//    تایپ‌های زیر (که فقط عدد و برچسبِ ثابت می‌پذیرند) و `sanitizeProps` در
//    زمانِ اجرا.
//
// ۳. **صف ماندگار نمی‌شود.** با بسته شدنِ اپ هرچه نرفته گم می‌شود — عمدی.
//    ذخیره کردنش یعنی رویدادهای هفته‌ی پیش که یک‌باره می‌رسند و نمودارِ
//    روزانه را دروغ می‌کنند. آمارِ ناقصِ درست‌تاریخ، بهتر از کاملِ بدتاریخ است.
import { supabase } from './supabase';
import { platformInfo } from '../platform';
import { reportError, setReportSession } from './report';
import {
  FLUSH_MS, isRepeatScreen, makeQueue, newSessionId, sanitizeProps, stampSender,
  type EventRow,
} from './analyticsQueue';
import {
  analyticsLabel, classifyEntry, isNewSession, normalizeScreen, rotateSession, sessionEndSignals,
  type Entry, type SessionClock,
} from './journeyLogic';
import { keyOfText } from './i18n';
import { observeTaps } from './devlog';
import { dwellToRecord, type ItemLink } from './itemWatch';
import { deviceId } from './deviceId';
import { noteGuestOpen, type GateReason, type NudgeTrigger } from './signupNudge';
import {
  funnelKey, isRepeatFunnel, openOrigin, type FunnelName, type FunnelProps, type OpenOrigin,
} from './funnel';

/**
 * رویدادهایی که ثبت می‌شوند.
 *
 * عمداً کوتاه است. فهرستِ بلندِ بی‌مصرف بدتر از نبودنش است: هر رویداد باید
 * سوالی را جواب بدهد که واقعاً می‌پرسیم. اضافه کردن آسان است، پس با کم
 * شروع می‌کنیم.
 *
 * هیچ‌کدام متنِ آزاد نمی‌گیرند — `screen` الگوی مسیر است نه محتوا، و `kind`
 * یکی از انواعِ ثابتِ سیگنال.
 */
export type AnalyticsEvent =
  | { name: 'screen_view'; props: { screen: string; from?: string; via?: string } }
  /** اولین رویدادِ هر نشست: از کجا آمد. */
  | { name: 'session_start'; props: { entry: string; ref?: string } }
  /** آخرین رویدادِ هر نشست: کجا رهایش کرد و چقدر آن‌جا ماند. */
  | { name: 'session_end'; props: { reason: string; ms: number } }
  /** یک دکمه/چیپ/سوییچ. `label` کلیدِ ترجمه است، نه متنِ دیده‌شده. */
  | { name: 'tap'; props: { label: string } }
  | {
      name: 'signal_created';
      props: {
        kind: string; recipients: number; emails: number; phones: number; audience: string;
      };
    }
  | { name: 'invite_responded'; props: { response: string } }
  | { name: 'auth_completed'; props: { method: string } }
  | { name: 'item_opened'; props: { kind: string } }
  /** چند میلی‌ثانیه روی صفحه‌ی یک رخداد/مکان ماند (فقط زمانِ جلوی چشم). */
  | { name: 'item_dwell'; props: { kind: string; id: string; ms: number } }
  /** روی کدام لینکِ بیرونیِ صفحه‌ی رخداد زد — `label` یکی از `ItemLink`. */
  | { name: 'item_link'; props: { kind: string; id: string; label: ItemLink } }
  /**
   * پیشنهادِ ثبت‌نام به مهمان (WHOSUP-308). `how` روشِ پیشنهاد است — `gate`
   * (برگه‌ی لحظه‌ی نیاز) یا `nudge` (نوارِ پایین) — و `action` یکی از
   * `shown` / `signup` / `later`، تا هر روش جدا سنجیده شود. `why` دلیلِ
   * برگه یا ماشه‌ی نوار.
   */
  | { name: 'signup_prompt'; props: { how: 'gate' | 'nudge'; action: 'shown' | 'signup' | 'later'; why: GateReason | NudgeTrigger } }
  /** پنج قدمِ قیف — شکل‌هایشان در `funnel.ts`. */
  | { [N in FunnelName]: { name: N; props: FunnelProps[N] } }[FunnelName];

export type EventName = AnalyticsEvent['name'];

const queue = makeQueue();
let timer: ReturnType<typeof setTimeout> | null = null;
/** شناسه، آخرین فعالیت و نشستِ پایان‌یافته — قاعده‌اش در `journeyLogic`. */
const clock: SessionClock = { id: newSessionId(), lastActivity: null, endedId: null };
/** بدونِ این، هر رویدادِ قبل از ورودِ کاربر `user_id` خالی می‌گرفت. */
let userId: string | null = null;
/** آخرین صفحه‌ی ثبت‌شده — فقط برای تشخیصِ تکرار. */
let lastScreen: string | null = null;

/* ─────────────────── حالتِ «سفر»: ترتیب، صفحه، نشست ─────────────────── */

/** شمارنده‌ی رویدادهای همین نشست. ترتیب را نگه می‌دارد وقتی زمان نمی‌تواند. */
let seq = 0;
/** صفحه‌ای که کاربر **الان** رویش است — هر رویدادی با همین مهر می‌خورد. */
let currentScreen: string | null = null;
/** صفحه‌ی پیش از `currentScreen` — مبنای «از کجا آمد»ِ `event_open`. */
let prevScreen: string | null = null;
/** کِی وارد این صفحه شد. مبنای «چقدر این‌جا ماند». */
let screenAt = 0;
/** آخرین لمس، برای اینکه بشود گفت کاربر با **کدام دکمه** رفت به صفحه‌ی بعد. */
let lastTap: { label: string; at: number } | null = null;
/** «از کجا آمد» — یک بار در بوتِ هر اپ پر می‌شود. */
let entry: Entry = { kind: 'direct' };

/**
 * لمسی که تا این‌قدر پیش از تعویضِ صفحه بوده، همان است که صفحه را باز کرد.
 * همان پنجره‌ای که `lib/devlog` برای `via` می‌گیرد و به همان دلیل: بیشتر از
 * این یعنی نسبت دادنِ یک ناوبری به دکمه‌ای که ربطی نداشته.
 */
const TAP_WINDOW = 2_000;

const now = (): number => Date.now();

/**
 * شناسه‌ی کاربر. `SessionProvider` صدایش می‌زند.
 *
 * ⚠️ سیاستِ نوشتنِ `app_events` می‌گوید `user_id` باید دقیقاً برابرِ
 * `auth.uid()` باشد (مایگریشن 0068). اگر این عقب بماند، ردیف‌ها بی‌صدا رد
 * می‌شوند — نه خطایی، نه داده‌ای.
 */
export const setAnalyticsUser = (id: string | null): void => { userId = id; };

/** کاربری که سابقه‌ی مهمانِ این دستگاه همین نشست به او وصل شد. */
let linkedFor: string | null = null;

/**
 * سابقه‌ی مهمانِ همین نصب را به حسابِ تازه‌وارد وصل می‌کند (WHOSUP-308).
 *
 * تصمیمِ صریحِ پرویز: «وصل کن» — تا معلوم شود کدام مهمان بعداً عضو شد و
 * پیش از آن چه دید. ردیف‌هایی که `user_id` دارند دست نمی‌خورند؛ فقط
 * ردیف‌های بی‌صاحبِ همین `install_id`. اعلامِ «به هویت وصل است» در
 * فرم‌های حریمِ خصوصیِ اپ‌استور و پلی لازمه‌ی همین تابع است.
 *
 * ⚠ با شناسه‌ی **نمایه** صدا زده می‌شود، نه نشست: `app_events.user_id` به
 * `profiles` کلید دارد و حسابِ تازه یک لحظه نشست دارد ولی نمایه نه — آن
 * لحظه به‌روزرسانی با خطای کلیدِ خارجی کلاً رد می‌شد.
 */
export async function linkGuestHistory(profileId: string): Promise<void> {
  if (linkedFor === profileId) return;
  linkedFor = profileId;
  try {
    const install = await deviceId();
    if (!install) return;
    const { error } = await supabase.rpc('link_guest_events', { p_install: install });
    if (error) reportError('analytics.link', error);
  } catch (e) {
    reportError('analytics.link', e);
  }
}

async function send(rows: EventRow[]): Promise<void> {
  if (!rows.length) return;
  try {
    /**
     * «الان کی وارد است» را از **خودِ کلاینت** می‌پرسیم و نه از `userId`ِ
     * بالا: همان نشستی که این درخواست را امضا می‌کند `auth.uid()` را تعیین
     * می‌کند، و سیاستِ `app_events` دقیقاً با همان می‌سنجد. `userId` از
     * یک `useEffect` پر می‌شود و می‌تواند یک تیک عقب باشد؛ این‌جا حدس زدن
     * یعنی همان دسته‌ی ردشده‌ی قبلی با یک قدم تأخیر.
     *
     * ⚠ `getSession()` از حافظه/انبارِ محلی می‌خواند و درخواستِ تازه‌ای
     * نمی‌زند مگر توکن منقضی شده باشد — همان کاری که خودِ `insert` یک خط
     * پایین‌تر هرحال می‌کند.
     */
    const { data: auth } = await supabase.auth.getSession();
    const stamped = stampSender(rows, auth.session?.user?.id ?? null);
    // شناسه‌ی نصب (WHOSUP-308، مایگریشن 0160) — همان عددِ تصادفیِ
    // `deviceId`، نه شناسه‌ی تبلیغاتیِ گوشی. بدونش رفتارِ مهمان هیچ نخی
    // نداشت که بعد از ثبت‌نام به حسابش وصل شود.
    const install = await deviceId();
    const { error } = await supabase.from('app_events')
      .insert(stamped.map((r) => ({ ...r, install_id: install })));
    // خطا فقط گزارش می‌شود و دوباره تلاش نمی‌کنیم: صفِ تلاشِ دوباره یعنی
    // همان مشکلِ «رویدادهای دیرآمده» که بالا ازش پرهیز کردیم.
    if (error) reportError('analytics.insert', error);
  } catch (e) {
    reportError('analytics.send', e);
  }
}

function scheduleFlush(): void {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    void send(queue.take());
  }, FLUSH_MS);
}

/**
 * یک رویداد را ثبت می‌کند و فوراً برمی‌گردد.
 *
 * هیچ‌وقت خطا پرت نمی‌کند — حتی اگر خودِ ثبت بترکد.
 */
export function track<E extends AnalyticsEvent>(name: E['name'], props: E['props']): void {
  try {
    const clean = sanitizeProps(props as Record<string, unknown>);
    // نگاه کن به `isRepeatScreen` — جلوی دوباره‌شماریِ StrictMode را می‌گیرد.
    if (isRepeatScreen(lastScreen, name, clean)) return;
    rotateIfIdle();
    if (name === 'screen_view') {
      const screen = String(clean.screen ?? '');
      lastScreen = screen;
      if (currentScreen !== screen) prevScreen = currentScreen;
      currentScreen = screen;
      screenAt = now();
    }
    push(name, clean);
  } catch (e) {
    reportError('analytics.track', e);
  }
}

/**
 * ساختنِ ردیف و انداختنش در صف. تنها جایی که `queue.push` صدا زده می‌شود.
 *
 * `screen` روی **هر** رویداد می‌نشیند و نه فقط `screen_view`: سوالِ «کاربر
 * روی این صفحه چه دکمه‌هایی را زد» بدونش یک join به نزدیک‌ترین ردیفِ قبلی
 * می‌خواست، که هم گران است هم روی مرزِ دو صفحه غلط جواب می‌دهد.
 */
function push(name: string, clean: Record<string, unknown>): void {
  const p = platformInfo();
  clock.lastActivity = now();
  // ⚠ `label` و `ms` از `props` **بیرون کشیده** می‌شوند و آن‌جا نمی‌مانند:
  // ستونِ واقعی هم ایندکس می‌خورد هم در SQL بی‌کست خوانده می‌شود، و دو
  // نسخه‌ی یک مقدار همان چیزی است که یک روز از هم دور می‌افتند.
  const { label, ms, ...rest } = clean;
  const full = queue.push({
    user_id: userId,
    session_id: clock.id,
    surface: p.isWeb ? 'web' : 'mobile',
    name,
    props: rest,
    app_version: p.appVersion,
    occurred_at: new Date().toISOString(),
    seq: seq++,
    screen: currentScreen,
    label: typeof label === 'string' ? label : null,
    ms: typeof ms === 'number' ? Math.round(ms) : null,
  });
  if (full) void send(full);
  else scheduleFlush();
}

/**
 * اگر از آخرین رویداد نیم‌ساعت گذشته، این یک نشستِ **تازه** است.
 *
 * قاعده‌اش در `journeyLogic.isNewSession` است و آن‌جا آزموده می‌شود. اثرش
 * این‌جاست: شناسه‌ی تازه، شمارنده از صفر، و یک `session_start` که
 * می‌گوید ورودش این بار مستقیم بوده — چون واقعاً بوده؛ کاربر خودش اپ را
 * دوباره باز کرده، از هیچ لینکی نیامده.
 */
function rotateIfIdle(): void {
  const cold = clock.lastActivity === null;
  if (!rotateSession(clock, now(), newSessionId)) return;
  pushSessionStart(cold);
}

/** اثرِ شروعِ نشستِ تازه: شمارنده از صفر و ورودِ «مستقیم» اگر اجرای سرد نیست. */
function pushSessionStart(cold: boolean): void {
  seq = 0;
  if (!cold) entry = { kind: 'direct' };
  push('session_start', sanitizeProps({ entry: entry.kind, ...(entry.ref ? { ref: entry.ref } : {}) }));
}

/** هرچه در صف مانده را همین حالا می‌فرستد — برای رفتنِ اپ به پس‌زمینه. */
export function flushAnalytics(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  void send(queue.take());
}

/** فقط برای تست. در محصول، نشست تا بسته شدنِ اپ یکی است. */
export const resetAnalyticsSession = (): string => { clock.id = newSessionId(); return clock.id; };
export const currentSession = (): string => clock.id;
// گزارشِ «عکس باز نشد» با همین نشست شمرده می‌شود (0161).
setReportSession(currentSession);

/* ══════════════════════ سفرِ کاربر ══════════════════════ */

/**
 * «از کجا آمد» — یک بار در بوتِ هر اپ، **پیش از** اولین رویداد.
 *
 * وب آدرسِ صفحه و `document.referrer` را می‌دهد؛ موبایل آدرسِ لینکِ عمیقی
 * که اپ را باز کرده (یا هیچ، که یعنی از آیکن). دسته‌بندی‌اش در
 * `journeyLogic.classifyEntry` است و همان‌جا آزموده می‌شود.
 */
export function setEntry(url: string, referrer?: string | null): void {
  try {
    entry = classifyEntry(url, referrer ?? null);
  } catch (e) {
    reportError('analytics.entry', e);
  }
}

/**
 * تعویضِ صفحه. هر دو اپ از روترِ خودشان صدایش می‌زنند.
 *
 * سه چیز با هم ثبت می‌شود و همین سه‌تا «سفر» را می‌سازند: کجا رفت، از کجا
 * آمد، و با کدام دکمه. بدونِ `from` هر نمودارِ جریانی باید ترتیب را از
 * ردیف‌های مجاور حدس بزند؛ بدونِ `via` معلوم نمی‌شود کاربر از کارت رفت یا
 * از منو.
 */
export function trackScreen(raw: string): void {
  const screen = normalizeScreen(raw);
  const from = currentScreen;
  const tap = lastTap && now() - lastTap.at < TAP_WINDOW ? lastTap.label : null;
  track('screen_view', {
    screen,
    ...(from && from !== screen ? { from } : {}),
    ...(tap ? { via: tap } : {}),
  });
}

/**
 * یک لمس. از `tapped()`ِ `lib/devlog` می‌آید، یعنی از همان جایی که هر دو
 * کیتِ رابط قبلاً دکمه‌هایشان را به آن وصل کرده‌اند — پس هیچ دکمه‌ای لازم
 * نیست کاری بکند تا شمرده شود.
 *
 * ⚠ `raw` متنِ دیده‌شده‌ی دکمه است و **همین‌طوری هرگز ذخیره نمی‌شود**:
 * `analyticsLabel` کلیدِ ترجمه‌اش را می‌گذارد و هر چیزِ ناشناخته را
 * `other` می‌کند.
 */
export function trackTap(raw: string): void {
  const label = analyticsLabel(raw, keyOfText);
  lastTap = { label, at: now() };
  track('tap', { label });
}

/**
 * لمسی که مستقیم از DOM آمده — برای دکمه‌ای که از کیت نیست.
 *
 * ═══ چرا لازم شد ═══
 *
 * `tapped()` فقط دکمه‌های کیت را می‌گیرد، و شمردنشان نشان داد ۱۰۴ دکمه‌ی
 * وب (در ۲۹ فایل) اصلاً از کیت نمی‌آیند — از جمله چیپ‌های دسته‌بندیِ صفحه‌ی
 * کشف، یعنی پرکارترین دکمه‌های اپ. جدولی که آن‌ها را نداشته باشد نمی‌گوید
 * «کم زده شدند»، می‌گوید «وجود ندارند» — و این بدترین شکلِ عددِ غلط است.
 *
 * ⚠ دکمه‌ی کیت **دو بار** ثبت نمی‌شود: کیت پیش از هندلر ثبت می‌کند و همان
 * کلیک چند میلی‌ثانیه بعد به `document` هم می‌رسد. پنجره‌ی کوتاهِ زیر دومی
 * را می‌اندازد. مبنا `lastTap` است، که همان چیزی است که `via`ِ ناوبری هم
 * از آن می‌آید.
 */
const DOM_TAP_DEDUPE_MS = 150;

export function trackDomTap(raw: string, trusted = false): void {
  if (lastTap && now() - lastTap.at < DOM_TAP_DEDUPE_MS) return;
  // ⚠ `allowRaw` فقط وقتی که برچسب را خودمان روی عنصر نوشته باشیم
  // (`data-tap`). متنِ خامِ دکمه هر چیزی می‌تواند باشد — نگاه کن به
  // توضیحِ `analyticsLabel`.
  const label = analyticsLabel(raw, keyOfText, trusted);
  lastTap = { label, at: now() };
  track('tap', { label });
}

/**
 * اپ رفت پس‌زمینه (یا تبِ مرورگر پنهان شد).
 *
 * `ms` مدتِ ماندن روی صفحه‌ی فعلی است. این تنها راهِ دانستنِ مدتِ **آخرین**
 * صفحه‌ی هر نشست است: بقیه‌ی صفحه‌ها را می‌شود از فاصله‌ی دو `screen_view`
 * حساب کرد، ولی آخری صفحه‌ی بعدی ندارد. بی این رویداد، «کجا رهایش کرد»
 * همیشه بی‌زمان می‌ماند.
 */
export function trackSessionEnd(reason: string): void {
  try {
    // تصمیمِ شروع/پایان در `journeyLogic.sessionEndSignals` است و آن‌جا با
    // رفت‌وبرگشتِ پس‌زمینه آزموده می‌شود (WHOSUP-317).
    const signals = sessionEndSignals(clock, now(), newSessionId);
    if (signals.includes('session_start')) pushSessionStart(false);
    if (!signals.includes('session_end')) return;
    const ms = screenAt ? now() - screenAt : 0;
    track('session_end', { reason, ms });
    flushAnalytics();
  } catch (e) {
    reportError('analytics.end', e);
  }
}

/**
 * اپ برگشت جلوی چشم.
 *
 * دو کار می‌کند و هر دو لازم‌اند: زمانِ صفحه از **حالا** شمرده می‌شود (وگرنه
 * نیم‌ساعت خوابِ گوشی به‌عنوانِ «نیم‌ساعت خواندنِ این صفحه» ثبت می‌شد — همان
 * چیزی که در لاگِ توسعه یک کوئریِ ۲۹۷ ثانیه‌ای ساخته بود)، و اگر آن‌قدر
 * گذشته باشد که نشست عوض شود، صفحه‌ی فعلی در نشستِ تازه دوباره ثبت می‌شود
 * تا سفرِ جدید از جایی شروع شود که کاربر واقعاً ایستاده.
 */
export function trackResume(): void {
  try {
    const rotates = isNewSession(clock.lastActivity, now());
    screenAt = now();
    if (rotates && currentScreen) {
      const screen = currentScreen;
      lastScreen = null;   // وگرنه `isRepeatScreen` همین را تکرار می‌بیند
      track('screen_view', { screen });
    }
  } catch (e) {
    reportError('analytics.resume', e);
  }
}

/* ══════════════════════ تماشای رخداد (WHOSUP-297) ══════════════════════ */

/**
 * مدتِ ماندن روی صفحه‌ی یک رخداد/مکان — موقعِ ترکِ صفحه صدا زده می‌شود.
 *
 * شناسه‌ی رخداد داده‌ی شخصی نیست (صفحه‌ی عمومی است) و بدونش نمی‌شود گفت
 * کدام رخداد نگه‌مان داشت. مدت در ستونِ `ms` می‌نشیند (نگاه کن به `push`).
 */
export function trackItemDwell(kind: 'event' | 'place', id: string, ms: number): void {
  const rec = dwellToRecord(ms);
  if (rec === null) return;
  track('item_dwell', { kind, id, ms: rec });
}

/** کلیک روی یکی از لینک‌های بیرونیِ صفحه‌ی رخداد. برچسب در ستونِ `label`. */
export function trackItemLink(kind: 'event' | 'place', id: string, label: ItemLink): void {
  track('item_link', { kind, id, label });
}

/** پیشنهادِ ثبت‌نام به مهمان: نمایش، «ثبت‌نام» یا «بعداً» — نگاه کن به `signupNudge.ts`. */
export function trackSignupPrompt(
  how: 'gate' | 'nudge', action: 'shown' | 'signup' | 'later', why: GateReason | NudgeTrigger,
): void {
  track('signup_prompt', { how, action, why });
}

/* ══════════════════════ قیف (WHOSUP-291) ══════════════════════ */

/** مبدأیی که صفحه‌ی قبلی نمی‌تواند بگوید (ضربه روی اعلان) و تا کی معتبر است. */
let originHint: { from: OpenOrigin; at: number } | null = null;
let lastFunnel: { key: string; at: number } | null = null;

/**
 * پیش از ناوبری صدا زده می‌شود تا `event_open`ِ بعدی بداند از کجا آمده —
 * مثلاً ضربه روی اعلانِ پوش، که می‌تواند از هر صفحه‌ای باشد.
 */
export function markOpenOrigin(from: OpenOrigin): void {
  originHint = { from, at: now() };
}

/**
 * یکی از پنج قدمِ قیف. همان `track()`: فوراً برمی‌گردد و خطا پرت نمی‌کند.
 *
 * دو بارِ پشتِ‌سرِ‌همِ یک قدم برای یک شناسه یک بار شمرده می‌شود — افکتِ
 * دوباره‌ی StrictMode یا رفرشِ کوئری کاربر را دو بار وارد نکرده است.
 */
export function trackFunnel<N extends FunnelName>(name: N, props: FunnelProps[N]): void {
  try {
    const key = funnelKey(name, props as Record<string, unknown>);
    const at = now();
    if (isRepeatFunnel(lastFunnel, key, at)) return;
    lastFunnel = { key, at };
    track(name, props as never);
  } catch (e) {
    reportError('analytics.funnel', e);
  }
}

/**
 * `event_open` با مبدأِ حساب‌شده. `from` اگر داده شود (صفحه‌ی لینکِ عمومی
 * خودش می‌داند) همان می‌ماند؛ وگرنه نشانه‌ی اعلان، وگرنه صفحه‌ی قبلی.
 */
export function trackEventOpen(id: string | undefined, source: 'signal' | 'discover', from?: OpenOrigin): void {
  const hint = originHint && now() - originHint.at < 10_000 ? originHint.from : null;
  originHint = null;
  // شمارشِ «چند رویداد باز کرد» برای نوارِ پیشنهادِ ثبت‌نام — فقط مهمان.
  if (!userId && source === 'discover') void noteGuestOpen();
  trackFunnel('event_open', { ...(id ? { id } : {}), source, from: from ?? openOrigin(prevScreen, hint) });
}

/**
 * وصل کردنِ کیت‌های رابط به این‌جا.
 *
 * در همین سطحِ ماژول و نه از یک `init…()` — همان دلیلی که سرِ
 * `observeQueries` در `lib/devlog` نوشته شده: هر اپی که این فایل را import
 * کرده (هر دو، برای `flushAnalytics`) ثبتش هم انجام شده، و یک قدمِ
 * راه‌اندازیِ فراموش‌شدنی کمتر است.
 */
observeTaps(trackTap);
